import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { CreateDraftReturnNReship } from 'constants/gql/mutations/checkout/createDraftReturnNReship.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Create+Immediate+Return+or+NCR

// Maps OXS originOfOrder values to OIS StandardChannel enum values
const ORDER_CHANNEL_MAP = {
    web: 'WEB',
    mobileWeb: 'MOBILE_WEB'
};

/**
 * Creates an immediate return or NCR for the given order.
 * Routes to OIS GraphQL (CreateDraftReturnNReship) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {Object} payload - Return request data
 * @param {string} payload.returnOrderId - The order ID to return
 * @param {string} payload.returnType - The type of return (IR or NCR)
 * @param {string} payload.returnReason - Reason code for the return
 * @param {string} payload.originOfOrder - Order channel
 * @returns {Promise<Object>} The create draft return response
 * @throws {GraphQLException} Throws when OIS GraphQL call fails (network/transport error)
 * @throws {Error} Throws a domain-level Error (with errorCode and errorMessages) when OIS responseCode is not SUCCESS
 * @throws {Object} Throws the rejected response object from ufeApi.makeRequest (includes responseStatus and response data fields)
 */
const createIRorNCR = async payload => {
    const oxsUrl = '/api/selfReturn/createIRorNCR';

    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;

    if (globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'CreateDraftReturnNReship',
            query: CreateDraftReturnNReship,
            variables: {
                input: {
                    orderId: payload.returnOrderId,
                    returnType: payload.returnType,
                    reasonCode: payload.returnReason,
                    orderChannel: ORDER_CHANNEL_MAP[payload.originOfOrder] || payload.originOfOrder
                }
            }
        });

        const result = data?.createDraftReturnNReship;

        if (result?.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Create draft return failed');
            error.errorCode = result.responseCode;
            error.errorMessages = result.errors?.length ? result.errors.map(e => e.message) : result.message ? [result.message] : [];
            throw error;
        }

        return result ?? data;
    }

    const data = await ufeApi.makeRequest(oxsUrl, {
        method: 'POST',
        body: JSON.stringify(payload)
    });

    return data;
};

export default createIRorNCR;
